import React, { useState, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  Stack,
  Paper,
  Fab,
  Tooltip,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { useLocation, useNavigate } from 'react-router-dom';
import CustomCard from '../../../Shared/Components/CustomCard/CustomCard';
import CashDocumentCard from '../CashDocumentCard/CashDocumentCard';
import {
  useGetCashDocumentsByReservationQuery,
  useLazyGetCashDocumentPdfQuery,
} from '../../Service/extendedCashDocumentsApi';
import { useDeleteCashDocumentMutation } from '../../Service/cashDocumentsApi';
import { formatAmount, CashDocumentType } from '../../Types/cashDocumentTypes';
import type { CashDocumentsSectionProps, CashDocumentsSummary } from './CashDocumentsSection.types';
import { getCashDocumentsErrorMessage } from '../../Utils/cashDocumentsErrorMapping';
import { downloadCashDocumentPdf } from '../../Utils/cashDocumentPdfUtils';
import {
  buildCashDocumentCreatePath,
  buildCashDocumentEditPath,
} from '../../Utils/cashDocumentNavigation';

/**
 * Cash Documents Section Component
 *
 * Displays linked KP/KW documents for a reservation
 * Shows summary (total KP, total KW, balance) and individual document cards
 * Includes FAB to create new cash document with reservationId prefilled
 */
const CashDocumentsSection: React.FC<CashDocumentsSectionProps> = ({
  reservationId,
  compact = false,
  showCreateButton = true,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [deleteTimestamp, setDeleteTimestamp] = useState<number>(0);
  const deleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const {
    data: documents = [],
    isLoading,
    error,
  } = useGetCashDocumentsByReservationQuery(reservationId);

  const [deleteCashDocument] = useDeleteCashDocumentMutation();
  const [triggerGetCashDocumentPdf] = useLazyGetCashDocumentPdfQuery();
  const returnTo = `${location.pathname}${location.search}${location.hash}`;

  // Calculate summary
  const summary: CashDocumentsSummary = useMemo(() => {
    if (!documents.length) {
      return {
        totalKP: 0,
        totalKW: 0,
        balance: 0,
        count: 0,
        currency: 'PLN',
      };
    }

    const totalKP = documents
      .filter((doc) => doc.type === CashDocumentType.KP)
      .reduce((sum, doc) => sum + doc.amount, 0);

    const totalKW = documents
      .filter((doc) => doc.type === CashDocumentType.KW)
      .reduce((sum, doc) => sum + doc.amount, 0);

    return {
      totalKP,
      totalKW,
      balance: totalKP - totalKW,
      count: documents.length,
      currency: documents[0]?.currency || 'PLN',
    };
  }, [documents]);

  const handleCreate = () => {
    navigate(buildCashDocumentCreatePath(reservationId, returnTo));
  };

  const handleEdit = (id: number) => {
    navigate(buildCashDocumentEditPath(id, returnTo));
  };

  const handleDelete = async (id: number) => {
    const now = Date.now();

    // First click: set confirmation state
    if (deleteConfirmId !== id || now - deleteTimestamp > 3000) {
      // Clear any existing timeout
      if (deleteTimeoutRef.current) {
        clearTimeout(deleteTimeoutRef.current);
      }

      setDeleteConfirmId(id);
      setDeleteTimestamp(now);

      // Auto-reset after 3 seconds if no second click
      deleteTimeoutRef.current = setTimeout(() => {
        setDeleteConfirmId(null);
        deleteTimeoutRef.current = null;
      }, 3000);

      return;
    }

    // Second click within 3 seconds: execute delete
    // Clear timeout since we're executing
    if (deleteTimeoutRef.current) {
      clearTimeout(deleteTimeoutRef.current);
      deleteTimeoutRef.current = null;
    }

    try {
      await deleteCashDocument(id).unwrap();
      setDeleteConfirmId(null);
    } catch (err) {
      console.error('Failed to delete cash document:', err);
      alert(getCashDocumentsErrorMessage(err, 'Nie udało się usunąć dokumentu. Spróbuj ponownie.'));
      setDeleteConfirmId(null);
    }
  };

  const handleDownloadPdf = async (id: number) => {
    try {
      const doc = documents.find((d) => d.id === id);
      await downloadCashDocumentPdf(
        triggerGetCashDocumentPdf,
        id,
        `${doc?.documentNumber || 'dokument'}.pdf`
      );
    } catch (err) {
      console.error('PDF download failed:', err);
      alert(getCashDocumentsErrorMessage(err, 'Nie udało się pobrać PDF. Spróbuj ponownie.'));
    }
  };

  if (isLoading) {
    return (
      <CustomCard title="Dokumenty kasowe (KP/KW)" icon={<ReceiptIcon color="primary" />}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      </CustomCard>
    );
  }

  if (error) {
    return (
      <CustomCard title="Dokumenty kasowe (KP/KW)" icon={<ReceiptIcon color="primary" />}>
        <Alert severity="error">
          {getCashDocumentsErrorMessage(
            error,
            'Nie udało się załadować dokumentów kasowych.'
          )}
        </Alert>
      </CustomCard>
    );
  }

  // Don't show section if no documents and compact mode
  if (!documents.length && compact) {
    return null;
  }

  return (
    <CustomCard title="Dokumenty kasowe (KP/KW)" icon={<ReceiptIcon color="primary" />}>
      <Stack spacing={3}>
        {/* Summary */}
        {documents.length > 0 && (
          <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }} >
                <Typography variant="caption" color="text.secondary">
                  Przyjęcia (KP)
                </Typography>
                <Typography variant="h6" color="success.main" fontWeight="bold">
                  {formatAmount(summary.totalKP, summary.currency)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }} >
                <Typography variant="caption" color="text.secondary">
                  Wydatki (KW)
                </Typography>
                <Typography variant="h6" color="error.main" fontWeight="bold">
                  {formatAmount(summary.totalKW, summary.currency)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }} >
                <Typography variant="caption" color="text.secondary">
                  Saldo
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  color={
                    summary.balance > 0
                      ? 'success.main'
                      : summary.balance < 0
                        ? 'error.main'
                        : 'text.secondary'
                  }
                >
                  {formatAmount(summary.balance, summary.currency)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Empty State */}
        {!documents.length && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Brak dokumentów kasowych dla tej rezerwacji
            </Typography>
            {showCreateButton && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreate}
                sx={{ mt: 2 }}
              >
                Dodaj dokument
              </Button>
            )}
          </Box>
        )}

        {/* Documents Grid */}
        {documents.length > 0 && (
          <>
            <Divider />
            <Grid container spacing={2}>
              {documents.map((doc) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={doc.id}>
                  <CashDocumentCard
                    document={doc}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onDownloadPdf={handleDownloadPdf}
                    compact={compact}
                    isDeleteConfirm={deleteConfirmId === doc.id}
                  />
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Stack>

      {/* Floating Action Button (only if documents exist and showCreateButton enabled) */}
      {showCreateButton && documents.length > 0 && (
        <Tooltip title="Dodaj dokument kasowy">
          <Fab
            color="primary"
            aria-label="add cash document"
            onClick={handleCreate}
            sx={{
              position: 'fixed',
              bottom: 32,
              right: 32,
              zIndex: 1000,
            }}
          >
            <AddIcon />
          </Fab>
        </Tooltip>
      )}
    </CustomCard>
  );
};

export default CashDocumentsSection;
